import { HttpErrorResponse } from '@angular/common/http';
import { IErrorDetails } from './error-handler-interfaces.service';

/**
 * An error object that when thrown allows the ability to set a custom message for display and custom logging options.
 * This is as opposed to throwing a normal `Error`, which will just display a generic message. Throwing `CustomError` causes
 * NGX-Error-Handler to capture the message, display it, and log it using the provided logging options.
 * @param customMessage - The message to display to the user.
 * @param errorOptions - Details about the error and how to log it.
 * @example // Basic usage:
 * throw (new CustomError('Whoops~ 😱'));
 * @example // Advanced usage:
 * throw new CustomError(`We're sorry 😭`, {
 *    showSupportId: true,
 *    level: 'exception',
 *    user: {email: myUser.email},
 *    tags: {'idiot', false}
 *    extra: {'custom_thing': 'custom value'}
 * });
 */
export class CustomError extends Error {
    // TODO: Can we not duplicate this JS Doc and have it continue working? Makes me think we need encapsulation?
    /** The error object's type, which is checked by the handler. */
    type = 'CustomError';
    /** The message to be used in the error and displayed to the user. */
    message: string;
    /** Details about the error and how to log it, including `showSupportId`, `errorObject`, `level`, `user`, `tags`, and `extra`. */
    errorDetails: IErrorDetails = {};

    constructor (
        /** The message to be used in the error and displayed to the user. */
        _customMessage: string,
        /** Details about the error and how to log it, including `showSupportId`, `originalError`, `level`, `user`, `tags`, and `extra`. */
        _errorOptions: IErrorDetails = { notificationPriority: 'minor' }
    ) {
        super(_customMessage);

        // If an error object is passed in, and it's an HTTP error
        if (_errorOptions.errorObject) {
            const errorObject = _errorOptions.errorObject as HttpErrorResponse;
            // ...clean up Angular's headers.
            const headersContent = {};
            if (errorObject.headers && errorObject.headers.keys) {
                errorObject.headers.keys().map((key) => {
                    headersContent[key] = errorObject.headers.get(key);
                });
            }
            _errorOptions.extra = { ..._errorOptions.extra, ...headersContent };
            // ...And check for a correlation ID
            const tagsContent = {};
            if (errorObject.headers && errorObject.headers.get && errorObject.headers.get('x-correlation-id')) {
                tagsContent['correlation-id'] = errorObject.headers.get('x-correlation-id');
            }
            _errorOptions.tags = { ..._errorOptions.tags, ...tagsContent };
        }

        // restore prototype chain
        // CustomError.prototype = Object.create(Error.prototype);
        // CustomError.prototype.constructor = CustomError;

        // init the error values.
        this.message = _customMessage;
        this.errorDetails = _errorOptions;
        this.errorDetails.showSupportId = _errorOptions.showSupportId ? _errorOptions.showSupportId : false;
        this.errorDetails.level = _errorOptions.level ? _errorOptions.level : 'error';
    }
}
